// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Debris Ops Store (Zustand)
// Manages load tickets, invoices, audit log, photos
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { fetchRecords, queryRecords } from '../api/client';
import { TABLES, FIELDS, createRecord, updateRecord, deleteRecord } from '../lib/api-switch';
import { useAuthStore } from './authStore';
import type {
  DebrisLoadTicket, DebrisInvoice, DebrisAuditEntry, DebrisPhoto, DebrisRequest,
} from '../types/debris';

// ── Field key shortcuts ──────────────────────────────────────
const FL = FIELDS.debrisLoadTickets;
const FI = FIELDS.debrisInvoices;
const FP = FIELDS.debrisPhotos;
// Note: debrisAuditLog and debrisRequests tables do not exist in V2

const TL = TABLES.debrisLoadTickets;
const TI = TABLES.debrisInvoices;
const TP = TABLES.debrisPhotos;
// Note: debrisAuditLog and debrisRequests tables do not exist in V2

function getToken() {
  return useAuthStore.getState().token ?? undefined;
}

// ── Stop photo extraction ──────────────────────────────────────
// Photos are stored OUTSIDE the stops JSON (in the notes meta field) so each
// gets its own TEXT column's 65K budget. Keys: sp_0_b, sp_0_a, sp_1_b, etc.
// On write we extract photos out; on read we inject them back in.

function extractStopPhotos(json: string): { cleanJson: string; photoMeta: Record<string, string> } {
  if (!json) return { cleanJson: '', photoMeta: {} };
  try {
    const stops: Record<string, unknown>[] = JSON.parse(json);
    if (!Array.isArray(stops)) return { cleanJson: json, photoMeta: {} };
    const photoMeta: Record<string, string> = {};
    const cleaned = stops.map((s, i) => {
      const out = { ...s };
      if (out.beforePhotoData && typeof out.beforePhotoData === 'string' && (out.beforePhotoData as string).length > 100) {
        photoMeta[`sp_${i}_b`] = out.beforePhotoData as string;
      }
      if (out.afterPhotoData && typeof out.afterPhotoData === 'string' && (out.afterPhotoData as string).length > 100) {
        photoMeta[`sp_${i}_a`] = out.afterPhotoData as string;
      }
      delete out.beforePhotoData;
      delete out.afterPhotoData;
      return out;
    });
    return { cleanJson: JSON.stringify(cleaned), photoMeta };
  } catch { return { cleanJson: json, photoMeta: {} }; }
}

function injectStopPhotos(json: string, meta: Record<string, string>): string {
  if (!json) return json;
  try {
    const stops: Record<string, unknown>[] = JSON.parse(json);
    if (!Array.isArray(stops)) return json;
    const injected = stops.map((s, i) => {
      const out = { ...s };
      if (meta[`sp_${i}_b`]) out.beforePhotoData = meta[`sp_${i}_b`];
      if (meta[`sp_${i}_a`]) out.afterPhotoData = meta[`sp_${i}_a`];
      return out;
    });
    return JSON.stringify(injected);
  } catch { return json; }
}

// ── Notes encoding for fields that don't have V2 columns ──
// Stashes extra fields into the notes TEXT field using triple-pipe markers.
// Decoded on read, encoded on write.
const TICKET_META_RE = /\|\|\|TMETA\|\|\|([\s\S]*?)\|\|\|ENDTMETA\|\|\|/;

function decodeTicketNotes(raw: string): { notes: string; meta: Record<string, string> } {
  if (!raw) return { notes: '', meta: {} };
  const m = raw.match(TICKET_META_RE);
  if (!m) return { notes: raw, meta: {} };
  try {
    const meta = JSON.parse(m[1]);
    const notes = raw.replace(TICKET_META_RE, '').trim();
    return { notes, meta };
  } catch {
    return { notes: raw, meta: {} };
  }
}

function encodeTicketNotes(userNotes: string, metaFields: Record<string, string>): string {
  // Remove any existing meta block from userNotes first
  const clean = userNotes.replace(TICKET_META_RE, '').trim();
  // Only add meta if there are non-empty values
  const nonEmpty = Object.fromEntries(Object.entries(metaFields).filter(([, v]) => v !== '' && v !== undefined));
  if (Object.keys(nonEmpty).length === 0) return clean;
  const metaStr = JSON.stringify(nonEmpty);
  return clean ? `${clean}\n|||TMETA|||${metaStr}|||ENDTMETA|||` : `|||TMETA|||${metaStr}|||ENDTMETA|||`;
}

// ── Row mappers ──────────────────────────────────────────────

function mapTicket(row: Record<string, unknown>): DebrisLoadTicket {
  const rid = Number(row.record_id) || Number(row.id) || 0;
  // Decode TMETA for fields that still don't have real columns
  // Decode TMETA only to get the user-visible notes text (strip the blob) and
  // any dynamic stop-photo keys that can't be separate columns.
  const rawNotes = String(row[FL.notes] ?? '');
  const { notes, meta } = decodeTicketNotes(rawNotes);
  return {
    id: rid, Rid: rid,
    incidentId:      String(row[FL.incidentId] ?? ''),
    ticketNumber:    String(row[FL.ticketNumber] ?? ''),
    truckId:         String(row[FL.truckId] ?? ''),
    truckNumber:     String(row[FL.truckNumber] ?? ''),
    crewId:          String(row[FL.crewId] ?? ''),
    contractorId:    String(row[FL.contractorId] ?? ''),
    zoneId:          String(row[FL.zoneId] ?? ''),
    disposalSiteId:  String(row[FL.disposalSiteId] ?? ''),
    facilityId:      FL.facilityId ? String(row[FL.facilityId] ?? '') : '',
    debrisType:      String(row[FL.debrisType] ?? ''),
    measurementMode: String(row[FL.measurementMode] ?? '') || 'volumetric',
    loadPercentage:  String(row[FL.loadPercentage] ?? ''),
    calculatedCubicYards: String(row[FL.calculatedCubicYards] ?? ''),
    unitCount:       String(row[FL.unitCount] ?? ''),
    unitSubcategory: String(row[FL.unitSubcategory] ?? ''),
    diameterRange:   String(row[FL.diameterRange] ?? ''),
    weight:          String(row[FL.weight] ?? ''),
    weightUnit:      String(row[FL.weightUnit] ?? ''),
    scaleTicketNumber: String(row[FL.scaleTicketNumber] ?? ''),
    pickupLat:       String(row[FL.pickupLat] ?? ''),
    pickupLng:       String(row[FL.pickupLng] ?? ''),
    pickupAddress:   String(row[FL.pickupAddress] ?? ''),
    pickupTime:      String(row[FL.pickupTime] ?? ''),
    pickupStopsJson: injectStopPhotos(String(row[FL.pickupStopsJson] ?? ''), meta),
    disposalTime:    String(row[FL.disposalTime] ?? ''),
    loadingMonitorName: String(row[FL.loadingMonitorName] ?? ''),
    loadingMonitorSignature: String(row[FL.loadingMonitorSignature] ?? ''),
    driverPickupSignature: String(row[FL.driverPickupSignature] ?? ''),
    loadingLoadPct:  String(row[FL.loadingLoadPct] ?? ''),
    towerLoadPct:    String(row[FL.towerLoadPct] ?? ''),
    monitorName:     String(row[FL.towerMonitorName] ?? ''),
    monitorSignature: String(row[FL.towerMonitorSignature] ?? ''),
    driverSignature: String(row[FL.driverDeliverySignature] ?? ''),
    verifiedAt:      String(row[FL.verifiedAt] ?? ''),
    handLoaded:      String(row[FL.handLoaded] ?? ''),
    qrCode:          String(row[FL.qrCode] ?? ''),
    status:          (String(row[FL.status] ?? '') || 'draft') as DebrisLoadTicket['status'],
    photoBeforeUrl:  String(row[FL.photoBeforeUrl] ?? ''),
    photoAfterUrl:   String(row[FL.photoAfterUrl] ?? ''),
    photoLoadUrl:    String(row[FL.photoLoadUrl] ?? ''),
    notes,
    payCodeId:       String(row[FL.payCodeId] ?? '') || '',
    paProjectId:     String(row[FL.paProjectId] ?? '') || '',
    subcontractorName: String(row[FL.subcontractorName] ?? ''),
    clinCode:        String(row[FL.clinCode] ?? ''),
    clinDescription: String(row[FL.clinDescription] ?? ''),
    distanceMiles:   String(row[FL.distanceMiles] ?? ''),
    transactionRate: String(row[FL.transactionRate] ?? ''),
    extendedCost:    String(row[FL.extendedCost] ?? ''),
  };
}

const INVOICE_META_RE = /\|\|\|IMETA\|\|\|([\s\S]*?)\|\|\|ENDIMETA\|\|\|/;

function mapInvoice(row: Record<string, unknown>): DebrisInvoice {
  const rid = Number(row.record_id) || Number(row.id) || 0;
  const rawNotes = String(row[FI.reviewerNotes] ?? '');
  const metaMatch = rawNotes.match(INVOICE_META_RE);
  let meta: Record<string, string> = {};
  let notes = rawNotes;
  if (metaMatch) {
    try { meta = JSON.parse(metaMatch[1]); } catch { /* ignore */ }
    notes = rawNotes.replace(INVOICE_META_RE, '').trim();
  }
  return {
    id: rid, Rid: rid,
    incidentId: String(row[FI.incidentId] ?? ''),
    contractorId: String(row[FI.contractorId] ?? ''),
    invoiceNumber: String(row[FI.invoiceNumber] ?? ''),
    periodStart: String(row[FI.periodStart] ?? ''),
    periodEnd: String(row[FI.periodEnd] ?? ''),
    totalCubicYards: meta.totalCubicYards || '',
    totalAmount: String(row[FI.totalAmount] ?? ''),
    ticketIds: meta.ticketIds || '[]',
    lineItems: String(row[FI.lineItems] ?? '') || '[]',
    status: (String(row[FI.status] ?? '') || 'draft') as DebrisInvoice['status'],
    approvedBy: meta.approvedBy || '',
    notes,
    invoiceType: String(row[FI.invoiceType] ?? '') || undefined,
  };
}

function mapAudit(row: Record<string, unknown>): DebrisAuditEntry {
  // debrisAuditLog table does not exist in V2; this mapper is a placeholder
  const rid = Number(row.record_id) || Number(row.id) || 0;
  return {
    id: rid, Rid: rid,
    incidentId: String(row.incidentId ?? ''),
    entityType: String(row.entityType ?? ''),
    entityId: String(row.entityId ?? ''),
    action: String(row.action ?? ''),
    fieldChanged: String(row.fieldChanged ?? ''),
    oldValue: String(row.oldValue ?? ''),
    newValue: String(row.newValue ?? ''),
    actorName: String(row.actorName ?? ''),
    actorEmail: String(row.actorEmail ?? ''),
    actorRole: String(row.actorRole ?? ''),
  };
}

function mapPhoto(row: Record<string, unknown>): DebrisPhoto {
  const rid = Number(row.record_id) || Number(row.id) || 0;
  return {
    id: rid, Rid: rid,
    parentId: String(row[FP.ticketId] ?? ''),
    parentType: 'ticket',
    incidentId: String(row[FP.incidentId] ?? ''),
    photoUrl: (() => { const v = String(row[FP.photo] ?? ''); return v.startsWith('data:') || v.startsWith('http') || v.startsWith('/') ? v : ''; })(),
    caption: String(row[FP.caption] ?? ''),
    geoLat: String(row[FP.latitude] ?? ''),
    geoLng: String(row[FP.longitude] ?? ''),
    takenAt: String(row[FP.takenAt] ?? ''),
    uploadedBy: String(row[FP.takenBy] ?? ''),
  };
}

function mapRequest(row: Record<string, unknown>): DebrisRequest {
  // debrisRequests table does not exist in V2; this mapper is a placeholder
  const rid = Number(row.record_id) || Number(row.id) || 0;
  return {
    id: rid, Rid: rid,
    incidentId: String(row.incidentId ?? ''),
    assessmentId: String(row.assessmentId ?? ''),
    address: String(row.address ?? ''),
    latitude: String(row.latitude ?? ''),
    longitude: String(row.longitude ?? ''),
    damageCategory: String(row.damageCategory ?? ''),
    damageTypes: String(row.damageTypes ?? '') || '[]',
    description: String(row.description ?? ''),
    status: (String(row.status ?? '') || 'pending') as DebrisRequest['status'],
    taggedBy: String(row.taggedBy ?? ''),
    taggedAt: String(row.taggedAt ?? ''),
    actionedBy: String(row.actionedBy ?? ''),
    actionedAt: String(row.actionedAt ?? ''),
    createdTicketId: String(row.createdTicketId ?? ''),
    notes: String(row.notes ?? ''),
  };
}

const PAGE_SIZE = 100;

// ── Store Interface ──────────────────────────────────────────

interface DebrisOpsStore {
  loadTickets: DebrisLoadTicket[];
  ticketsTotal: number;
  ticketsPage: number;
  invoices: DebrisInvoice[];
  auditLog: DebrisAuditEntry[];
  photos: DebrisPhoto[];
  requests: DebrisRequest[];
  loadingTickets: boolean;
  loadingInvoices: boolean;
  loadingAudit: boolean;
  loadingPhotos: boolean;
  loadingRequests: boolean;
  error: string | null;

  // Load Tickets
  loadLoadTickets: () => Promise<void>;
  setTicketsPage: (page: number) => void;
  createLoadTicket: (data: Partial<DebrisLoadTicket>) => Promise<DebrisLoadTicket | null>;
  updateLoadTicket: (id: number, data: Partial<DebrisLoadTicket>) => Promise<void>;
  deleteLoadTicket: (id: number) => Promise<void>;

  // Invoices
  loadInvoices: () => Promise<void>;
  createInvoice: (data: Partial<DebrisInvoice>) => Promise<DebrisInvoice | null>;
  updateInvoice: (id: number, data: Partial<DebrisInvoice>) => Promise<void>;
  deleteInvoice: (id: number) => Promise<void>;

  // Requests (Impact → Debris handoff)
  loadRequests: () => Promise<void>;
  createRequest: (data: Partial<DebrisRequest>) => Promise<DebrisRequest | null>;
  updateRequest: (id: number, data: Partial<DebrisRequest>) => Promise<void>;
  deleteRequest: (id: number) => Promise<void>;

  // Audit Log
  loadAuditLog: () => Promise<void>;
  createAuditEntry: (data: Partial<DebrisAuditEntry>) => Promise<void>;

  // Photos
  loadPhotos: () => Promise<void>;
  createPhoto: (data: Partial<DebrisPhoto>) => Promise<DebrisPhoto | null>;
  deletePhoto: (id: number) => Promise<void>;
}

// ── Store Implementation ─────────────────────────────────────

export const useDebrisOpsStore = create<DebrisOpsStore>((set, get) => ({
  loadTickets: [],
  ticketsTotal: 0,
  ticketsPage: 1,
  invoices: [],
  auditLog: [],
  photos: [],
  requests: [],
  loadingTickets: false,
  loadingInvoices: false,
  loadingAudit: false,
  loadingPhotos: false,
  loadingRequests: false,
  error: null,

  // ── Load Tickets ──────────────────────────────────────────

  loadLoadTickets: async () => {
    set({ loadingTickets: true, error: null });
    try {
      const { ticketsPage } = get();
      const inc = useAuthStore.getState().activeIncident;
      const incId = inc?.id?.toString() ?? '';
      const incFilter = incId ? [{ field: FL.incidentId, operator: 'eq' as const, value: incId }] : [];
      const res = await queryRecords<Record<string, unknown>>(TL, {
        limit: PAGE_SIZE,
        offset: (ticketsPage - 1) * PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
        ...(incFilter.length > 0 ? { filters: incFilter } : {}),
      });
      // GAB filter API is unreliable — also filter client-side
      const filtered = res.records.map(mapTicket).filter(t => !incId || t.incidentId === incId);
      set({ loadTickets: filtered, ticketsTotal: res.total, loadingTickets: false });
      // Sync ticket data to debris-store so both stores have consistent data.
      // One-way only (ops -> debris); debris-store does NOT sync back to avoid loops.
      import('./debris-store').then(m => {
        const ds = m.useDebrisStore.getState();
        if (!ds.ticketsLoading) ds.fetchTickets();
      }).catch(() => {});
    } catch (err) {
      set({ loadingTickets: false, error: err instanceof Error ? err.message : 'Failed to load tickets' });
    }
  },

  setTicketsPage: (page) => {
    set({ ticketsPage: page });
    get().loadLoadTickets();
  },

  createLoadTicket: async (data) => {
    // Stop photos are dynamic-keyed and still stored in notes TMETA
    const { cleanJson: cleanStopsJson, photoMeta: stopPhotos } = extractStopPhotos(data.pickupStopsJson ?? '');
    const notesWithMeta = encodeTicketNotes(data.notes ?? '', stopPhotos);
    const fields: Array<[string, string]> = [
      [FL.incidentId,     data.incidentId ?? ''],
      [FL.ticketNumber,   data.ticketNumber ?? ''],
      [FL.truckId,        data.truckId ?? ''],
      [FL.contractorId,   data.contractorId ?? ''],
      [FL.zoneId,         data.zoneId ?? ''],
      [FL.disposalSiteId, data.disposalSiteId ?? ''],
      [FL.debrisType,     data.debrisType ?? ''],
      [FL.measurementMode,data.measurementMode ?? ''],
      [FL.status,         data.status ?? 'draft'],
      [FL.notes,          notesWithMeta],
    ];
    const rid = await createRecord(TL, fields, getToken());
    if (rid) {
      const extra: Array<[string, string]> = [
        [FL.calculatedCubicYards, data.calculatedCubicYards ?? ''],
        [FL.unitCount,        data.unitCount ?? ''],
        [FL.weight,           data.weight ?? ''],
        [FL.pickupStopsJson,  cleanStopsJson],
        [FL.loadingMonitorName, data.loadingMonitorName ?? ''],
        [FL.loadingMonitorSignature, data.loadingMonitorSignature ?? ''],
        [FL.driverPickupSignature, data.driverPickupSignature ?? ''],
        [FL.loadingLoadPct,   data.loadingLoadPct ?? ''],
        [FL.towerLoadPct,     data.towerLoadPct ?? ''],
        [FL.handLoaded,       data.handLoaded ?? ''],
        [FL.truckNumber,      data.truckNumber ?? ''],
        [FL.pickupLat,        data.pickupLat ?? ''],
        [FL.pickupLng,        data.pickupLng ?? ''],
        [FL.pickupAddress,    data.pickupAddress ?? ''],
        [FL.pickupTime,       data.pickupTime ?? ''],
        [FL.crewId,           data.crewId ?? ''],
        [FL.loadPercentage,   data.loadPercentage ?? ''],
        [FL.disposalTime,     data.disposalTime ?? ''],
        [FL.disposalDate,     (data as any).disposalDate ?? ''],
        [FL.photoBeforeUrl,   data.photoBeforeUrl ?? ''],
        [FL.photoAfterUrl,    data.photoAfterUrl ?? ''],
        [FL.photoLoadUrl,     data.photoLoadUrl ?? ''],
        [FL.unitSubcategory,  data.unitSubcategory ?? ''],
        [FL.diameterRange,    data.diameterRange ?? ''],
        [FL.weightUnit,       data.weightUnit ?? ''],
        [FL.scaleTicketNumber,data.scaleTicketNumber ?? ''],
        [FL.qrCode,           data.qrCode ?? ''],
      ];
      // Phase 4 — Directory FK fields: only write when provisioned
      if (FL.contractorOrgId && (data as any).contractorOrgId) extra.push([FL.contractorOrgId, String((data as any).contractorOrgId)]);
      if (FL.facilityId && (data as any).facilityId) extra.push([FL.facilityId, String((data as any).facilityId)]);
      if (FL.monitorPersonId && (data as any).monitorPersonId) extra.push([FL.monitorPersonId, String((data as any).monitorPersonId)]);
      if (FL.paProjectId && (data as any).paProjectId) extra.push([FL.paProjectId, String((data as any).paProjectId)]);
      if (FL.payCodeId && (data as any).payCodeId) extra.push([FL.payCodeId, String((data as any).payCodeId)]);
      await updateRecord(TL, rid, extra, getToken());
      const extra2: Array<[string, string]> = [
        [FL.verifiedAt, data.verifiedAt ?? ''],
        [FL.towerMonitorName, data.monitorName ?? (data as any).towerMonitorName ?? ''],
        [FL.towerMonitorSignature, data.monitorSignature ?? (data as any).towerMonitorSignature ?? ''],
        [FL.driverDeliverySignature, data.driverSignature ?? (data as any).driverDeliverySignature ?? ''],
      ];
      await updateRecord(TL, rid, extra2, getToken());
    }
    await get().loadLoadTickets();
    return get().loadTickets.find(t => t.id === rid) ?? null;
  },

  updateLoadTicket: async (id, data) => {
    // Get the existing ticket to preserve its current meta
    const existing = get().loadTickets.find(t => t.id === id);
    const existingNotes = existing?.notes ?? '';

    // Stop photos are dynamic-keyed and still stored in notes TMETA
    const { cleanJson: cleanStopsJson, photoMeta: stopPhotos } = data.pickupStopsJson !== undefined
      ? extractStopPhotos(data.pickupStopsJson)
      : { cleanJson: undefined, photoMeta: {} as Record<string, string> };

    // Preserve existing stop photo meta keys when stops aren't being updated
    const existingStopPhotos: Record<string, string> = {};
    if (existing?.pickupStopsJson) {
      const { photoMeta: prevPhotos } = extractStopPhotos(existing.pickupStopsJson);
      Object.assign(existingStopPhotos, prevPhotos);
    }

    const userNotes = data.notes !== undefined ? data.notes : existingNotes;
    const notesWithMeta = encodeTicketNotes(userNotes, { ...existingStopPhotos, ...stopPhotos });

    const fields: Array<[string, string]> = [
      [FL.notes,             notesWithMeta],
      [FL.truckNumber,       data.truckNumber       ?? existing?.truckNumber       ?? ''],
      [FL.pickupLat,         data.pickupLat         ?? existing?.pickupLat         ?? ''],
      [FL.pickupLng,         data.pickupLng         ?? existing?.pickupLng         ?? ''],
      [FL.pickupAddress,     data.pickupAddress     ?? existing?.pickupAddress     ?? ''],
      [FL.pickupTime,        data.pickupTime        ?? existing?.pickupTime        ?? ''],
      [FL.subcontractorName, data.subcontractorName ?? existing?.subcontractorName ?? ''],
      [FL.clinCode,          data.clinCode          ?? existing?.clinCode          ?? ''],
      [FL.clinDescription,   (data as any).clinDescription ?? (existing as any)?.clinDescription ?? ''],
      [FL.distanceMiles,     data.distanceMiles     ?? existing?.distanceMiles     ?? ''],
      [FL.transactionRate,   data.transactionRate   ?? existing?.transactionRate   ?? ''],
      [FL.extendedCost,      data.extendedCost      ?? existing?.extendedCost      ?? ''],
      [FL.crewId,            data.crewId            ?? existing?.crewId            ?? ''],
      [FL.loadPercentage,    data.loadPercentage    ?? existing?.loadPercentage    ?? ''],
      [FL.disposalTime,      data.disposalTime      ?? existing?.disposalTime      ?? ''],
      [FL.photoBeforeUrl,    data.photoBeforeUrl    ?? existing?.photoBeforeUrl    ?? ''],
      [FL.photoAfterUrl,     data.photoAfterUrl     ?? existing?.photoAfterUrl     ?? ''],
      [FL.photoLoadUrl,      data.photoLoadUrl      ?? existing?.photoLoadUrl      ?? ''],
      [FL.unitSubcategory,   data.unitSubcategory   ?? existing?.unitSubcategory   ?? ''],
      [FL.diameterRange,     data.diameterRange     ?? existing?.diameterRange     ?? ''],
      [FL.weightUnit,        data.weightUnit        ?? existing?.weightUnit        ?? ''],
      [FL.scaleTicketNumber, data.scaleTicketNumber ?? existing?.scaleTicketNumber ?? ''],
      [FL.qrCode,            data.qrCode            ?? existing?.qrCode            ?? ''],
    ];
    if (data.ticketNumber !== undefined) fields.push([FL.ticketNumber, data.ticketNumber]);
    if (data.truckId !== undefined) fields.push([FL.truckId, data.truckId]);
    if (data.contractorId !== undefined) fields.push([FL.contractorId, data.contractorId]);
    if (data.zoneId !== undefined) fields.push([FL.zoneId, data.zoneId]);
    if (data.disposalSiteId !== undefined) fields.push([FL.disposalSiteId, data.disposalSiteId]);
    if (data.debrisType !== undefined) fields.push([FL.debrisType, data.debrisType]);
    if (data.measurementMode !== undefined) fields.push([FL.measurementMode, data.measurementMode]);
    if (data.calculatedCubicYards !== undefined) fields.push([FL.calculatedCubicYards, data.calculatedCubicYards]);
    if (data.unitCount !== undefined) fields.push([FL.unitCount, data.unitCount]);
    if (data.weight !== undefined) fields.push([FL.weight, data.weight]);
    if (data.status !== undefined) fields.push([FL.status, data.status]);
    if (cleanStopsJson !== undefined) fields.push([FL.pickupStopsJson, cleanStopsJson]);
    if (data.loadingMonitorName !== undefined) fields.push([FL.loadingMonitorName, data.loadingMonitorName]);
    if (data.loadingMonitorSignature !== undefined) fields.push([FL.loadingMonitorSignature, data.loadingMonitorSignature]);
    if (data.driverPickupSignature !== undefined) fields.push([FL.driverPickupSignature, data.driverPickupSignature]);
    if (data.loadingLoadPct !== undefined) fields.push([FL.loadingLoadPct, data.loadingLoadPct]);
    if (data.towerLoadPct !== undefined) fields.push([FL.towerLoadPct, data.towerLoadPct]);
    if (data.handLoaded !== undefined) fields.push([FL.handLoaded, data.handLoaded]);
    if (data.verifiedAt !== undefined) fields.push([FL.verifiedAt, data.verifiedAt]);
    // Tower monitor fields — stored in V2 columns (not meta)
    if (data.monitorName !== undefined) fields.push([FL.towerMonitorName, data.monitorName]);
    if (data.monitorSignature !== undefined) fields.push([FL.towerMonitorSignature, data.monitorSignature]);
    if (data.driverSignature !== undefined) fields.push([FL.driverDeliverySignature, data.driverSignature]);
    if ((data as any).towerMonitorName !== undefined) fields.push([FL.towerMonitorName, (data as any).towerMonitorName]);
    if ((data as any).towerMonitorSignature !== undefined) fields.push([FL.towerMonitorSignature, (data as any).towerMonitorSignature]);
    if ((data as any).driverDeliverySignature !== undefined) fields.push([FL.driverDeliverySignature, (data as any).driverDeliverySignature]);
    if (FL.payCodeId && (data as any).payCodeId !== undefined) fields.push([FL.payCodeId, String((data as any).payCodeId ?? '')]);
    if (FL.paProjectId && (data as any).paProjectId !== undefined) fields.push([FL.paProjectId, String((data as any).paProjectId ?? '')]);
    if (FL.facilityId && (data as any).facilityId !== undefined) fields.push([FL.facilityId, String((data as any).facilityId ?? '')]);
    if (FL.contractorOrgId && (data as any).contractorOrgId !== undefined) fields.push([FL.contractorOrgId, String((data as any).contractorOrgId ?? '')]);
    if (FL.monitorPersonId && (data as any).monitorPersonId !== undefined) fields.push([FL.monitorPersonId, String((data as any).monitorPersonId ?? '')]);

    if (fields.length > 0) {
      for (let i = 0; i < fields.length; i += 10) {
        await updateRecord(TL, id, fields.slice(i, i + 10), getToken());
      }
      await get().loadLoadTickets();
    }

    // Phase 4 — Auto-create PA ProjectCost when ticket is verified (best effort)
    if (data.status === 'verified' && TABLES.paProjectCosts) {
      try {
        const ticket = get().loadTickets.find(t => t.id === id) ?? existing;
        if (ticket) {
          const FC = FIELDS.paProjectCosts;
          // Compute a cost value from the ticket — use calculatedCubicYards as quantity, no rate stored
          const qty = parseFloat(ticket.calculatedCubicYards || ticket.unitCount || ticket.weight || '1') || 1;
          const mode = ticket.measurementMode || 'volumetric';
          const unit = mode === 'unit' ? (ticket.unitSubcategory || 'unit')
            : mode === 'weight' ? (ticket.weightUnit || 'lbs')
            : 'CY';
          const description = `Debris removal — ${ticket.ticketNumber || `#${id}`} (${ticket.debrisType || 'mixed'})`;
          const costPayload: Record<string, unknown> = {
            [FC.costType]:    'debris',
            [FC.description]: description,
            [FC.quantity]:    qty,
            [FC.unit]:        unit,
            [FC.totalCost]:   0, // Rate unknown without pay code — operator fills in
            [FC.category]:    'debris_removal',
          };
          if (FC.sourceModule)   costPayload[FC.sourceModule]   = 'debris';
          if (FC.sourceRecordId) costPayload[FC.sourceRecordId] = String(id);
          if (FC.projectId && ticket.paProjectId) costPayload[FC.projectId] = ticket.paProjectId;
          await createRecord(TABLES.paProjectCosts, costPayload);
        }
      } catch { /* best effort — non-blocking */ }
    }
  },

  deleteLoadTicket: async (id) => {
    if (!id) return;
    await deleteRecord(TL, id, getToken());
    set({ loadTickets: get().loadTickets.filter(t => t.id !== id) });
  },

  // ── Invoices ──────────────────────────────────────────────

  loadInvoices: async () => {
    set({ loadingInvoices: true, error: null });
    try {
      const inc = useAuthStore.getState().activeIncident;
      const incId = inc?.id?.toString() ?? '';
      const incFilter = incId ? [{ field: FI.incidentId, operator: 'eq' as const, value: incId }] : [];
      const res = incFilter.length > 0
        ? await queryRecords<Record<string, unknown>>(TI, { filters: incFilter, limit: 200 })
        : await fetchRecords<Record<string, unknown>>(TI, { limit: 200 });
      // GAB filter API is unreliable — also filter client-side
      set({ invoices: res.records.map(mapInvoice).filter(i => (!incId || i.incidentId === incId) && i.status !== 'sub_invoice'), loadingInvoices: false });
    } catch (err) {
      set({ loadingInvoices: false, error: err instanceof Error ? err.message : 'Failed to load invoices' });
    }
  },

  createInvoice: async (data) => {
    // Note: totalCubicYards, ticketIds, approvedBy have no V2 columns — stored in reviewerNotes as meta
    const metaStr = JSON.stringify({
      totalCubicYards: data.totalCubicYards ?? '',
      ticketIds: data.ticketIds ?? '[]',
      approvedBy: data.approvedBy ?? '',
    });
    const notesWithMeta = data.notes ? `${data.notes}\n|||IMETA|||${metaStr}|||ENDIMETA|||` : `|||IMETA|||${metaStr}|||ENDIMETA|||`;
    const fields: Array<[string, unknown]> = [
      [FI.incidentId, data.incidentId ?? ''],
      [FI.contractorId, data.contractorId ?? ''],
      [FI.invoiceNumber, data.invoiceNumber ?? ''],
      [FI.periodStart, data.periodStart ?? ''],
      [FI.periodEnd, data.periodEnd ?? ''],
      [FI.totalAmount, parseFloat(data.totalAmount ?? '0') || 0],
      [FI.lineItems, data.lineItems ?? '[]'],
      [FI.status, data.status ?? 'draft'],
      [FI.reviewerNotes, notesWithMeta],
    ];
    const rid = await createRecord(TI, fields, getToken());
    await get().loadInvoices();
    return get().invoices.find(i => i.id === rid) ?? null;
  },

  updateInvoice: async (id, data) => {
    const fields: Array<[string, unknown]> = [];
    if (data.contractorId !== undefined) fields.push([FI.contractorId, data.contractorId]);
    if (data.invoiceNumber !== undefined) fields.push([FI.invoiceNumber, data.invoiceNumber]);
    if (data.periodStart !== undefined) fields.push([FI.periodStart, data.periodStart]);
    if (data.periodEnd !== undefined) fields.push([FI.periodEnd, data.periodEnd]);
    if (data.totalAmount !== undefined) fields.push([FI.totalAmount, parseFloat(data.totalAmount) || 0]);
    if (data.lineItems !== undefined) fields.push([FI.lineItems, data.lineItems]);
    if (data.status !== undefined) fields.push([FI.status, data.status]);
    // Store non-V2-column fields in reviewerNotes meta
    if (data.totalCubicYards !== undefined || data.ticketIds !== undefined || data.approvedBy !== undefined || data.notes !== undefined) {
      const existing = get().invoices.find(i => i.id === id);
      const metaStr = JSON.stringify({
        totalCubicYards: data.totalCubicYards ?? existing?.totalCubicYards ?? '',
        ticketIds: data.ticketIds ?? existing?.ticketIds ?? '[]',
        approvedBy: data.approvedBy ?? existing?.approvedBy ?? '',
      });
      const userNotes = data.notes !== undefined ? data.notes : (existing?.notes ?? '');
      const notesWithMeta = userNotes ? `${userNotes}\n|||IMETA|||${metaStr}|||ENDIMETA|||` : `|||IMETA|||${metaStr}|||ENDIMETA|||`;
      fields.push([FI.reviewerNotes, notesWithMeta]);
    }
    if (fields.length > 0) {
      await updateRecord(TI, id, fields, getToken());
      await get().loadInvoices();
    }
  },

  deleteInvoice: async (id) => {
    if (!id) return;
    await deleteRecord(TI, id, getToken());
    set({ invoices: get().invoices.filter(i => i.id !== id) });
  },

  // ── Requests (Impact → Debris) ───────────────────────────
  // Note: debrisRequests table does not exist in V2; these are no-ops

  loadRequests: async () => {
    set({ loadingRequests: false });
  },

  createRequest: async (_data) => {
    // debrisRequests table not in V2 — return null
    return null;
  },

  updateRequest: async (_id, _data) => {
    // debrisRequests table not in V2 — no-op
  },

  deleteRequest: async (_id) => {
    // debrisRequests table not in V2 — no-op
    set({ requests: [] });
  },

  // ── Audit Log ─────────────────────────────────────────────
  // Note: debrisAuditLog table does not exist in V2; these are no-ops

  loadAuditLog: async () => {
    set({ loadingAudit: false, auditLog: [] });
  },

  createAuditEntry: async (_data) => {
    // debrisAuditLog table not in V2 — no-op
  },

  // ── Photos ────────────────────────────────────────────────

  loadPhotos: async () => {
    set({ loadingPhotos: true, error: null });
    try {
      const inc = useAuthStore.getState().activeIncident;
      const incId = inc?.id?.toString() ?? '';
      const incFilter = incId ? [{ field: FP.incidentId, operator: 'eq' as const, value: incId }] : [];
      const res = incFilter.length > 0
        ? await queryRecords<Record<string, unknown>>(TP, { limit: 500, filters: incFilter })
        : await fetchRecords<Record<string, unknown>>(TP, { limit: 500 });
      const photos = res.records.map(mapPhoto).filter(p => !incId || String((p as any).incidentId ?? '') === incId);
      set({ photos, loadingPhotos: false });
    } catch (err) {
      set({ loadingPhotos: false, error: err instanceof Error ? err.message : 'Failed to load photos' });
    }
  },

  createPhoto: async (data) => {
    // V2 debrisPhotos columns: incidentId, ticketId, zoneId, photo, caption, category, latitude, longitude, takenBy, takenAt
    // parentId maps to ticketId; photoUrl maps to photo; geoLat/geoLng map to latitude/longitude; uploadedBy maps to takenBy
    const fields: Array<[string, string]> = [
      [FP.incidentId, data.incidentId ?? ''],
      [FP.ticketId, data.parentId ?? ''],
      [FP.photo, data.photoUrl ?? ''],
      [FP.caption, data.caption ?? ''],
      [FP.latitude, data.geoLat ?? ''],
      [FP.longitude, data.geoLng ?? ''],
      [FP.takenAt, data.takenAt ?? ''],
      [FP.takenBy, data.uploadedBy ?? ''],
    ];
    const rid = await createRecord(TP, fields, getToken());
    await get().loadPhotos();
    return get().photos.find(p => p.id === rid) ?? null;
  },

  deletePhoto: async (id) => {
    await deleteRecord(TP, id, getToken());
    set({ photos: get().photos.filter(p => p.id !== id) });
  },
}));
